/**
 * Email Verification page
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './VerifyEmail.css';

export default function VerifyEmail() {
    const { userId } = useParams<{ userId: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [verifying, setVerifying] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const verifyEmail = async () => {
            const code = searchParams.get('code');

            if (!code) {
                setError('Invalid verification link - missing code');
                setVerifying(false);
                return;
            }

            if (!userId) {
                setError('Invalid verification link - missing user ID');
                setVerifying(false);
                return;
            }

            if (!user) {
                setError('Please log in to verify your email');
                setVerifying(false);
                return;
            }

            if (user.id !== parseInt(userId)) {
                setError(
                    'You can only verify your own email address. Please log in with the correct account.'
                );
                setVerifying(false);
                return;
            }

            try {
                const response = await fetch('/api/auth/verify-email', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        userId: parseInt(userId),
                        code: parseInt(code),
                    }),
                });

                const data = await response.json();

                if (response.ok && data.success) {
                    setSuccess(true);
                    setVerifying(false);

                    // Redirect to user profile after 2 seconds
                    setTimeout(() => {
                        navigate(`/user/${userId}`);
                    }, 2000);
                } else {
                    setError(data.error || 'Verification failed');
                    setVerifying(false);
                }
            } catch (err) {
                setError('An error occurred during verification');
                setVerifying(false);
            }
        };

        verifyEmail();
    }, [userId, searchParams, user, navigate]);

    return (
        <div className="verifyEmailContainer">
            <div className="verifyEmailCard">
                {verifying && (
                    <>
                        <div className="verifyEmailSpinner"></div>
                        <h2>Verifying your email...</h2>
                        <p>Please wait while we verify your email address.</p>
                    </>
                )}

                {success && (
                    <>
                        <div className="verifyEmailSuccess">✓</div>
                        <h2>Email Verified!</h2>
                        <p>
                            Your email has been successfully verified. You will
                            be redirected to your profile shortly.
                        </p>
                    </>
                )}

                {error && (
                    <>
                        <div className="verifyEmailError">✗</div>
                        <h2>Verification Failed</h2>
                        <p>{error}</p>
                        <button
                            className="verifyEmailButton"
                            onClick={() => navigate('/')}
                        >
                            Go to Home
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
